/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Shared Page Designer style controls (font, size, color, alignment, margin bottom, image display).
//
// IMPORTANT: the cartridge generator reads decorator source text and does NOT evaluate
// expressions, so component decorators must repeat these enum values as literal arrays —
// never `values: [...STYLE_FONT_VALUES]`. The arrays here are the runtime source of truth
// and are checked against each component's metadata in style-attributes.test.ts.
import type { CSSProperties } from 'react';

export const STYLE_FONT_VALUES = ['Default', 'Playfair Display', 'Inter'] as const;
export const STYLE_SIZE_VALUES = ['Default', 'Small', 'Medium', 'Large', 'X-Large'] as const;
export const STYLE_ALIGN_VALUES = ['Default', 'Left', 'Center', 'Right'] as const;
export const STYLE_MARGIN_BOTTOM_VALUES = ['0', '2', '4', '6', '8', '12', '16'] as const;
export const STYLE_IMAGE_DISPLAY_VALUES = ['Crop to fit', 'Show full image'] as const;

export type TextRole = 'heading' | 'body';

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Returns the trimmed value when it is a valid #rgb / #rrggbb hex color, otherwise undefined. */
export function parseHexColor(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : undefined;
}

const FONT_FAMILY: Record<string, string> = {
    'Playfair Display': 'var(--font-display)',
    Inter: 'var(--font-sans)',
};

// Every breakpoint is set so these win over a component's own responsive size classes via tailwind-merge.
const SIZE_CLASSES: Record<TextRole, Record<string, string>> = {
    heading: {
        Small: 'text-xl md:text-xl lg:text-2xl xl:text-2xl',
        Medium: 'text-2xl md:text-3xl lg:text-4xl xl:text-4xl',
        Large: 'text-3xl md:text-4xl lg:text-5xl xl:text-5xl',
        'X-Large': 'text-4xl md:text-5xl lg:text-6xl xl:text-7xl',
    },
    body: {
        Small: 'text-xs md:text-xs lg:text-sm xl:text-sm',
        Medium: 'text-sm md:text-sm lg:text-base xl:text-base',
        Large: 'text-base md:text-base lg:text-lg xl:text-lg',
        'X-Large': 'text-lg md:text-lg lg:text-xl xl:text-xl',
    },
};

export interface TextStyleInput {
    fontStyle?: string;
    fontSize?: string;
    color?: string;
}

/** Class + inline style for one text element. Unset/'Default' values return nothing so the component's own styling stays. */
export function getTextStyle(
    { fontStyle, fontSize, color }: TextStyleInput,
    role: TextRole
): { className?: string; style: CSSProperties } {
    const style: CSSProperties = {};
    const fontFamily = fontStyle ? FONT_FAMILY[fontStyle] : undefined;
    if (fontFamily) style.fontFamily = fontFamily;
    const hex = parseHexColor(color);
    if (hex) style.color = hex;
    return { className: fontSize ? SIZE_CLASSES[role][fontSize] : undefined, style };
}

const ALIGN_CLASSES: Record<string, { text: string; items: string; justify: string }> = {
    Left: { text: 'text-left', items: 'items-start', justify: 'justify-start' },
    Center: { text: 'text-center', items: 'items-center', justify: 'justify-center' },
    Right: { text: 'text-right', items: 'items-end', justify: 'justify-end' },
};

/** text-align plus the matching flex alignment (for stacking CTAs); undefined for 'Default'. */
export function getAlignClasses(
    align: string | undefined
): { text: string; items: string; justify: string } | undefined {
    return align ? ALIGN_CLASSES[align] : undefined;
}

const MARGIN_BOTTOM_CLASSES: Record<string, string> = {
    '2': 'mb-2',
    '4': 'mb-4',
    '6': 'mb-6',
    '8': 'mb-8',
    '12': 'mb-12',
    '16': 'mb-16',
};

/** Root-element bottom margin; '0' (the default) adds nothing. */
export function getMarginBottomClass(marginBottom: string | undefined): string | undefined {
    return marginBottom ? MARGIN_BOTTOM_CLASSES[marginBottom] : undefined;
}

/** True when the merchant chose to show the image at its natural height instead of cropping. */
export function isFullImage(imageDisplay: string | undefined): boolean {
    return imageDisplay === 'Show full image';
}

/** Classes for an <img> rendered at natural height (no crop). */
export const FULL_IMAGE_CLASSES = 'w-full h-auto object-contain';
